import { SettingOutlined } from '@ant-design/icons';
import { Badge, Tooltip } from 'antd';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FaPause, FaPlay, FaStepBackward, FaStepForward } from 'react-icons/fa';
import { RiFullscreenExitLine, RiFullscreenFill } from 'react-icons/ri';
import { useNavigate } from 'react-router';
import { XmButton } from 'src/component/button/XmButton';
import { XmSelect } from 'src/component/select/XmSelect';
import { Log } from 'src/service/log.service';
import { DomUtil } from 'src/util/DomUtil';
import { AUTOPLAY_MIN_DASHBOARDS, AUTOPLAY_PRESETS_S, formatAutoplayInterval } from './autoplayConst';
import './DashboardAutoplay.less';
import { AutoplayQueueItem, loadFolderQueue } from './autoplayQueue';
import {
  advanceAutoplay, dashboardPath, enterTvMode, exitTvMode, getMountedDashboardId, openAutoplayModal,
  setAutoplayInterval, startAutoplay, toggleAutoplayPause, useAutoplayStore
} from './autoplayStore';

/**
 * Playback chrome for a folder of dashboards, rendered outside the top menu so it stays reachable
 * after a dashboard hides the rest of the chrome. Same controls whether or not playback runs.
 */
export const AutoplayControlBar: FC = () => {
  const navigate = useNavigate();
  const active = useAutoplayStore(s => s.active);
  const queue = useAutoplayStore(s => s.queue);
  const index = useAutoplayStore(s => s.index);
  const folderLabel = useAutoplayStore(s => s.folderLabel);
  const intervalS = useAutoplayStore(s => s.intervalS);
  const startIntervalS = useAutoplayStore(s => s.startIntervalS);
  const paused = useAutoplayStore(s => s.paused);
  const hidden = useAutoplayStore(s => s.hidden);
  // whole seconds only - the status text is the only thing that reads the countdown
  const secondsLeft = useAutoplayStore(s => Math.ceil(s.remainingMs / 1000));
  const waiting = useAutoplayStore(s => s.waitingSince != null);
  const tvActive = useAutoplayStore(s => s.tvActive);
  const folder = useAutoplayStore(s => s.folder);
  const barOpen = useAutoplayStore(s => s.barOpen);
  const [busy, setBusy] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const aborter = useRef<AbortController>(undefined);
  /** Queue loaded before playback started, so stepping by hand costs one request per folder. */
  const preloaded = useRef<{ folderId: string | null; count: number; items: AutoplayQueueItem[]; }>(undefined);

  const visible = active || (barOpen && folder != null && folder.dashboardCount >= AUTOPLAY_MIN_DASHBOARDS);

  useEffect(() => () => {
    aborter.current?.abort();
    document.documentElement.style.removeProperty('--xm-autoplay-bar-height');
  }, []);

  // the scroll area sizes itself from the viewport, so it has to know the bar is there
  useEffect(() => {
    const root = document.documentElement;
    const bar = barRef.current;
    if (!bar) {
      root.style.removeProperty('--xm-autoplay-bar-height');
      return;
    }
    // writing to documentElement invalidates styles for the whole document, so only on a real change
    const observer = new ResizeObserver(() => {
      const height = `${bar.offsetHeight}px`;
      if (root.style.getPropertyValue('--xm-autoplay-bar-height') === height) return;
      root.style.setProperty('--xm-autoplay-bar-height', height);
    });
    observer.observe(bar);
    return () => observer.disconnect();
  }, [visible]);

  // the session's own interval stays offered; memoised - XmSelect re-measures on a new identity
  const intervalOptions = useMemo(
    () => [...new Set([...AUTOPLAY_PRESETS_S, startIntervalS, intervalS])]
      .sort((a, b) => a - b)
      .map(seconds => ({ label: formatAutoplayInterval(seconds), value: seconds })),
    [startIntervalS, intervalS]);

  /** The tree endpoint backs both entry points, so every way in plays the same order. */
  const loadQueue = async (): Promise<AutoplayQueueItem[] | undefined> => {
    if (!folder) return;
    if (preloaded.current?.folderId === folder.folderId
      && preloaded.current.count === folder.dashboardCount) return preloaded.current.items;

    aborter.current?.abort();
    const request = new AbortController();
    aborter.current = request;
    setBusy(true);
    try {
      const items = await loadFolderQueue(folder.folderId, folder.folderKind, request.signal);
      if (request.signal.aborted) return;
      setBusy(false);
      // the user may have left this folder or closed the bar while the tree was loading
      const current = useAutoplayStore.getState();
      if (current.folder !== folder || !current.barOpen) return;
      if (!items || items.length < AUTOPLAY_MIN_DASHBOARDS) {
        Log.warn(`Folder "${folder.folderLabel}" no longer holds enough dashboards to play.`);
        return;
      }
      preloaded.current = { folderId: folder.folderId, count: folder.dashboardCount, items };
      return items;
    } catch (reason) {
      if (request.signal.aborted) return;
      setBusy(false);
      Log.error('Failed to list dashboards of the folder!', reason as Error);
    }
  };

  const togglePlay = () => {
    if (active) {
      toggleAutoplayPause();
      return;
    }
    void loadQueue().then(items => {
      if (items) startAutoplay(items, folder.folderLabel, useAutoplayStore.getState().intervalS);
    });
  };

  /** Stepping without a session is plain navigation - it must not start playback. */
  const step = (direction: number) => {
    if (active) {
      advanceAutoplay(direction, true);
      return;
    }
    void loadQueue().then(items => {
      if (!items) return;
      const at = items.findIndex(item => item.id === getMountedDashboardId());
      navigate(dashboardPath(items[(at + direction + items.length) % items.length].id));
    });
  };

  if (!visible) return null;

  const running = active && !waiting && !paused && !hidden;
  const nextLabel = queue[(index + 1) % queue.length]?.label;

  const status = !active ? ''
    : waiting ? `Loading ${queue[index]?.label}...`
      : paused ? `Paused - next: ${nextLabel}`
        : hidden ? `Waiting for this tab - next: ${nextLabel}`
          : `Next: ${nextLabel} in ${secondsLeft} s`;

  const position = active
    ? `${index + 1} / ${queue.length}`
    : `${folder.dashboardCount} dashboards`;

  return (
    <div className="xm-autoplay-bar" ref={barRef} role="region" aria-label="Dashboard autoplay">
      <div className="xm-autoplay-bar-row">
        <div className={`xm-autoplay-bar-title${active ? '' : ' xm-autoplay-bar-title-wide'}`}>
          <Badge className="xm-autoplay-bar-dot" status={running ? 'processing' : 'default'} />
          <strong>{active ? folderLabel : folder.folderLabel}</strong>
          <span className="xm-autoplay-bar-position">{position}</span>
        </div>

        {active && <div className="xm-autoplay-bar-status">{status}</div>}

        <div className="xm-autoplay-bar-actions">
          {/* ignoreReadonly: playback is a view action, a readonly user still drives it */}
          <XmSelect<number>
            ignoreReadonly
            showSearch={false}
            className="xm-autoplay-bar-interval"
            value={intervalS}
            onChange={setAutoplayInterval}
            aria-label="Switch interval"
            options={intervalOptions} />
          <Tooltip getPopupContainer={DomUtil.popupToBody} title="Previous dashboard">
            <XmButton ignoreReadonly icon={<FaStepBackward />} onClick={() => step(-1)} aria-label="Previous dashboard" />
          </Tooltip>
          <Tooltip getPopupContainer={DomUtil.popupToBody} title={running ? 'Pause' : active ? 'Resume' : 'Play'}>
            <XmButton
              ignoreReadonly
              type="primary"
              icon={active && !paused ? <FaPause /> : <FaPlay />}
              loading={busy}
              onClick={togglePlay}
              aria-label={active && !paused ? 'Pause autoplay' : 'Start autoplay'} />
          </Tooltip>
          <Tooltip getPopupContainer={DomUtil.popupToBody} title="Next dashboard">
            <XmButton ignoreReadonly icon={<FaStepForward />} onClick={() => step(1)} aria-label="Next dashboard" />
          </Tooltip>
          <Tooltip getPopupContainer={DomUtil.popupToBody} title={tvActive ? 'Leave TV mode' : 'TV mode: fill the screen'}>
            <XmButton
              ignoreReadonly
              icon={tvActive ? <RiFullscreenExitLine /> : <RiFullscreenFill />}
              onClick={() => tvActive ? exitTvMode() : void enterTvMode()}
              aria-label={tvActive ? 'Leave TV mode' : 'Enter TV mode'} />
          </Tooltip>
          <Tooltip getPopupContainer={DomUtil.popupToBody} title="Autoplay settings">
            <XmButton
              ignoreReadonly
              icon={<SettingOutlined />}
              onClick={() => folder && openAutoplayModal(
                { folderId: folder.folderId, folderKind: folder.folderKind, folderLabel: folder.folderLabel })}
              aria-label="Autoplay settings" />
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
